"use client"

import Image from "next/image"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Heart, Users, Lightbulb, Target, Sparkles, Home } from "lucide-react"
import Link from "next/link"
import { BeforeAfterSlider } from "@/app/components/BeforeAfterSlider"

const values = [
  {
    icon: Heart,
    title: "Accessibility First",
    description:
      "Beautiful design shouldn't be a luxury. We make professional-quality interior design accessible to everyone, regardless of budget or experience.",
  },
  {
    icon: Lightbulb,
    title: "Innovation with Purpose",
    description:
      "We harness cutting-edge AI technology not for its own sake, but to solve real problems and empower people to create spaces they love.",
  },
  {
    icon: Users,
    title: "Community Driven",
    description:
      "Our platform grows stronger with every user. We learn from your feedback and continuously improve to serve your design needs better.",
  },
]

export function AboutPage() {
  return (
    <div className="w-full min-h-screen overflow-x-hidden touch-pan-y">
      {/* Hero Section */}
      <section className="relative w-full py-16 md:py-24 bg-gradient-to-br from-[#40A1AB]/10 to-white touch-pan-y">
        <div className="container px-4 mx-auto md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl mb-6">
              Making Beautiful Design <span className="text-[#40A1AB]">Accessible to All</span>
            </h1>
            <p className="text-lg text-gray-600 md:text-xl leading-relaxed">
              We believe everyone deserves to live in a space they love. That's why we created Roomvia – to empower
              everyone to design their dream spaces through the power of AI.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="w-full py-16 md:py-20 touch-pan-y">
        <div className="container px-4 mx-auto md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold md:text-4xl">Our Story</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Roomvia's journey started with a splash—literally. In 2023 a burst bathroom pipe sent one of our
                  founders on an unplanned, insurance-funded remodel that paired them with a professional interior
                  designer. The finished spaces were gorgeous, but the rest of the house still needed love—and the
                  budget was gone. Armed with an AI background, that founder hacked together generative-design workflows
                  to stretch every remaining dollar. The results were eye-opening, but the process was far too technical
                  for most homeowners.
                </p>
                <p>
                  At the same time, two long-time colleagues—a seasoned full-stack developer and an enterprise
                  commercial leader—were eager to build something meaningful. We came together around a common belief:
                  professional-quality design should be as easy to access as streaming a movie.
                </p>
                <p>
                  Months of late-night prototyping, interviews with homeowners and designers, and countless model tweaks
                  later, <strong>Roomvia.ai</strong> was born. Today our platform translates your ideas into beautifully
                  curated rooms—no design degree, no daunting price tag, just the confidence to create a home that feels
                  unmistakably you.
                </p>
              </div>
            </div>
            <div className="relative h-[400px] md:h-[500px] rounded-lg overflow-hidden shadow-xl">
              <Image
                src="/images/about/roomvia-about.png"
                alt="From concept to reality - AI-powered interior design transformation"
                fill
                className="object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="w-full py-16 md:py-20 bg-gray-50 touch-pan-y">
        <div className="container px-4 mx-auto md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-[#40A1AB] p-4 rounded-full">
                <Target className="h-8 w-8 text-white" />
              </div>
            </div>
            <h2 className="text-3xl font-bold md:text-4xl mb-6">Our Mission</h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              To empower everyone to create beautiful, personalized spaces by making professional design guidance
              accessible, affordable, and enjoyable. We believe that with the right tools and inspiration, anyone can
              create a space that truly feels like home.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <div className="text-center">
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <Sparkles className="h-8 w-8 text-[#40A1AB] mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">Inspire Creativity</h3>
                  <p className="text-gray-600 text-sm">
                    Help people discover their unique style and bring their vision to life
                  </p>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <Home className="h-8 w-8 text-[#40A1AB] mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">Transform Spaces</h3>
                  <p className="text-gray-600 text-sm">
                    Turn any room into a beautiful, functional space that reflects who you are
                  </p>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <Users className="h-8 w-8 text-[#40A1AB] mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">Empower Everyone</h3>
                  <p className="text-gray-600 text-sm">
                    Make professional design accessible regardless of budget or experience
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="w-full py-16 md:py-20 touch-pan-y">
        <div className="container px-4 mx-auto md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold md:text-4xl mb-4">Our Values</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              These principles guide everything we do, from product development to customer support
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon
              return (
                <Card key={index} className="text-center">
                  <CardContent className="p-8">
                    <div className="bg-[#40A1AB]/10 p-4 rounded-full w-fit mx-auto mb-6">
                      <Icon className="h-8 w-8 text-[#40A1AB]" />
                    </div>
                    <h3 className="text-xl font-semibold mb-4">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="w-full py-16 md:py-20 touch-pan-y">
        <div className="container px-4 mx-auto md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative h-[400px] md:h-[500px] rounded-lg overflow-hidden shadow-xl">
              <BeforeAfterSlider
                beforeImage="/images/magic-original.png"
                afterImage="/images/magic-transformed.png"
                beforeAlt="Room before AI transformation"
                afterAlt="Room after AI transformation"
                className="w-full h-full rounded-lg"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl font-bold md:text-4xl">The Technology Behind the Magic</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Our AI models are trained on thousands of professionally designed spaces, learning the principles of
                  good design, color theory, spatial relationships, and style aesthetics.
                </p>
                <p>
                  But we don't just rely on algorithms. Our technology is enhanced by insights from real interior
                  designers, ensuring that every recommendation balances AI efficiency with human creativity and
                  expertise.
                </p>
                <p>
                  The result? Design suggestions that are not only beautiful but also practical, personalized, and
                  achievable within your space and budget.
                </p>
              </div>
              <div className="bg-[#40A1AB]/10 p-6 rounded-lg">
                <p className="text-[#40A1AB] font-medium">
                  "We believe AI should enhance human creativity, not replace it. Our technology empowers you to explore
                  possibilities you might never have considered."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full py-16 md:py-20 bg-[#40A1AB] text-white touch-pan-y">
        <div className="container px-4 mx-auto md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold md:text-4xl mb-6">Ready to Transform Your Space?</h2>
            <p className="text-lg opacity-90 mb-8">
              Join thousands of homeowners who have already discovered the joy of creating beautiful spaces with
              Roomvia.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/designer">
                <Button className="bg-white text-[#40A1AB] hover:bg-gray-100">Start Designing Now</Button>
              </Link>
              <Link href="/gallery">
                <Button className="bg-white text-[#40A1AB] hover:bg-gray-100">View Gallery</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
